import type React from 'react';
import { Spinner } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import { Navigate, Outlet } from 'react-router-dom';
import { isDeviceEnrolled } from '../utils';
import { PATH } from './PATH';
import { useSessionGuard } from './useSessionGuard';

/**
 * Three-state guard for the POS. The two credentials gate independently:
 * - terminal not enrolled → **enrollment** (exchange the branch API token).
 * - enrolled but no usable seller session → **seller-select** (pick + PIN).
 *   An expired access token with a refresh token is refreshed first.
 * - both present → render the POS.
 */
export const SecuredRoutes: React.FC = () => {
    if (!isDeviceEnrolled()) {
        return <Navigate replace to={PATH.ENROLL} />;
    }
    // Only an enrolled terminal goes on to restore its seller session.
    return <SessionGate />;
};

const SessionGate: React.FC = () => {
    const { t } = useTranslation('components');
    const session = useSessionGuard();

    if (session === 'signedOut') {
        return <Navigate replace to={PATH.LOGIN} />;
    }
    if (session === 'restoring') {
        return (
            <div
                className="flex min-h-dvh items-center justify-center"
                data-testid="secured-routes-restoring"
                id="secured-routes-restoring"
            >
                <Spinner aria-label={t('layout.restoringSession')} />
            </div>
        );
    }
    return <Outlet />;
};
